import React, { useState, useEffect } from 'react';
import { 
  Cloud, 
  Sun, 
  CloudRain, 
  CloudLightning, 
  Wind,
  Thermometer,
  Droplets,
  Eye,
  MapPin,
  RefreshCw
} from 'lucide-react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Fade,
  Slide,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';

const WeatherCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  }
}));

const WeatherIcon = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
  boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
}));

const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Get user's location
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
        },
        (error) => {
          console.log('Geolocation error:', error);
          // Fallback to Mumbai coordinates
          setLocation({ lat: 19.0760, lon: 72.8777 });
        }
      );
    } else {
      // Fallback to Mumbai coordinates
      setLocation({ lat: 19.0760, lon: 72.8777 });
    }
  };

  // Fetch weather data from Open-Meteo API
  const fetchWeather = async (lat, lon) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation_probability,weather_code,wind_speed_10m,visibility&hourly=temperature_2m,precipitation_probability,weather_code&timezone=auto`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch weather data');
      }
      
      const data = await response.json();
      
      // Transform Open-Meteo data to our format
      const weatherData = {
        location: 'Your Location', // We'll get city name later
        temperature: Math.round(data.current.temperature_2m),
        feelsLike: Math.round(data.current.apparent_temperature),
        condition: getWeatherCondition(data.current.weather_code),
        humidity: data.current.relative_humidity_2m,
        windSpeed: Math.round(data.current.wind_speed_10m),
        visibility: Math.round(data.current.visibility / 1000), // Convert to km
        uvIndex: 6, // Open-Meteo doesn't provide UV index in free tier
        precipitationProbability: data.current.precipitation_probability,
        weatherCode: data.current.weather_code
      };
      
      setWeather(weatherData);
    } catch (err) {
      console.error('Weather fetch error:', err);
      setError('Failed to load weather data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Get weather condition from WMO weather codes
  const getWeatherCondition = (code) => {
    const conditions = {
      0: 'Clear sky',
      1: 'Mainly clear',
      2: 'Partly cloudy',
      3: 'Overcast',
      45: 'Foggy',
      48: 'Depositing rime fog',
      51: 'Light drizzle',
      53: 'Moderate drizzle',
      55: 'Dense drizzle',
      61: 'Slight rain',
      63: 'Moderate rain',
      65: 'Heavy rain',
      71: 'Slight snow',
      73: 'Moderate snow',
      75: 'Heavy snow',
      77: 'Snow grains',
      80: 'Slight rain showers',
      81: 'Moderate rain showers',
      82: 'Violent rain showers',
      85: 'Slight snow showers',
      86: 'Heavy snow showers',
      95: 'Thunderstorm',
      96: 'Thunderstorm with slight hail',
      99: 'Thunderstorm with heavy hail'
    };
    return conditions[code] || 'Unknown';
  };

  const getWeatherIcon = (condition) => {
    const conditionLower = condition.toLowerCase();
    
    if (conditionLower.includes('rain') || conditionLower.includes('drizzle')) {
      return <CloudRain size={32} color="white" />;
    }
    if (conditionLower.includes('thunder')) {
      return <CloudLightning size={32} color="white" />;
    }
    if (conditionLower.includes('snow')) {
      return <CloudRain size={32} color="white" />; // Using rain icon for snow
    }
    if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
      return <Cloud size={32} color="white" />;
    }
    if (conditionLower.includes('fog')) {
      return <Cloud size={32} color="white" />;
    }
    return <Sun size={32} color="white" />;
  };

  const getWeatherAdvice = (condition, temperature, precipitationProbability) => {
    const conditionLower = condition.toLowerCase();
    
    if (conditionLower.includes('rain') || conditionLower.includes('drizzle') || precipitationProbability > 60) {
      return {
        advice: 'Consider carpooling to avoid getting wet',
        color: 'warning'
      };
    }
    
    if (conditionLower.includes('thunder')) {
      return {
        advice: 'Avoid outdoor activities, consider postponing ride',
        color: 'error'
      };
    }
    
    if (temperature > 35) {
      return {
        advice: 'Stay hydrated during your ride',
        color: 'error'
      };
    }
    
    if (temperature < 15) {
      return {
        advice: 'Dress warmly for your commute',
        color: 'info'
      };
    }
    
    if (conditionLower.includes('fog')) {
      return {
        advice: 'Drive carefully, reduced visibility',
        color: 'warning'
      };
    }
    
    return {
      advice: 'Perfect weather for ride sharing!',
      color: 'success'
    };
  };

  const handleRefresh = () => {
    setRefreshing(true);
    if (location) {
      fetchWeather(location.lat, location.lon);
    }
  };

  useEffect(() => {
    getUserLocation();
  }, []);

  useEffect(() => {
    if (location) {
      fetchWeather(location.lat, location.lon);
    }
  }, [location]);

  if (loading) {
    return (
      <WeatherCard>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2, color: '#6b7280' }}>
            Loading weather...
          </Typography>
        </CardContent>
      </WeatherCard>
    );
  }

  if (error) {
    return (
      <WeatherCard>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ color: '#ef4444', mb: 2 }}>
            {error}
          </Typography>
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={20} />
          </IconButton>
        </CardContent>
      </WeatherCard>
    );
  }

  const weatherAdvice = getWeatherAdvice(weather.condition, weather.temperature, weather.precipitationProbability);

  return (
    <Slide direction="up" in timeout={600}>
      <WeatherCard>
        <CardContent sx={{ p: 3 }}>
          <Fade in timeout={800}>
            <Box>
              {/* Header with refresh button */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MapPin size={16} color="#6366f1" />
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {weather.location}
                  </Typography>
                </Box>
                <Tooltip title="Refresh weather">
                  <IconButton 
                    onClick={handleRefresh} 
                    disabled={refreshing}
                    size="small"
                    sx={{ color: '#6366f1' }}
                  >
                    <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Location and Current Weather */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <WeatherIcon>
                  {getWeatherIcon(weather.condition)}
                </WeatherIcon>
                
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {weather.temperature}°C
                </Typography>
                
                <Typography variant="body1" sx={{ color: '#6b7280', mb: 2 }}>
                  {weather.condition}
                </Typography>
                
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  Feels like {weather.feelsLike}°C
                </Typography>
              </Box>

              {/* Weather Details */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Droplets size={16} color="#6366f1" />
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Humidity: {weather.humidity}%
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Wind size={16} color="#6366f1" />
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Wind: {weather.windSpeed} km/h
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Eye size={16} color="#6366f1" />
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Visibility: {weather.visibility} km
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudRain size={16} color="#6366f1" />
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Rain: {weather.precipitationProbability}%
                  </Typography>
                </Box>
              </Box>

              {/* Weather Advice */}
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={weatherAdvice.advice}
                  color={weatherAdvice.color}
                  sx={{ 
                    fontWeight: 600,
                    background: weatherAdvice.color === 'success' ? 'rgba(16, 185, 129, 0.1)' :
                                weatherAdvice.color === 'warning' ? 'rgba(245, 158, 11, 0.1)' :
                                weatherAdvice.color === 'error' ? 'rgba(239, 68, 68, 0.1)' :
                                'rgba(99, 102, 241, 0.1)',
                    color: weatherAdvice.color === 'success' ? '#10b981' :
                           weatherAdvice.color === 'warning' ? '#f59e0b' :
                           weatherAdvice.color === 'error' ? '#ef4444' :
                           '#6366f1',
                    border: `1px solid`,
                    borderColor: weatherAdvice.color === 'success' ? 'rgba(16, 185, 129, 0.3)' :
                                 weatherAdvice.color === 'warning' ? 'rgba(245, 158, 11, 0.3)' :
                                 weatherAdvice.color === 'error' ? 'rgba(239, 68, 68, 0.3)' :
                                 'rgba(99, 102, 241, 0.3)',
                  }}
                />
              </Box>

              {/* Attribution */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                  Weather data by <a href="https://open-meteo.com" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>Open-Meteo.com</a>
                </Typography>
              </Box>
            </Box>
          </Fade>
        </CardContent>
      </WeatherCard>
    </Slide>
  );
};

export default WeatherWidget; 